import { supabase } from '../supabase';
import { processAction } from '../gamification/gamificationService';
import { XP_ACTIONS } from '../gamification/xpConfig';
import { Database, Json } from '../../types/supabase';

// Tipos para o serviço de sessões de jogo
export interface GameSession {
  id: string;
  user_id: string;
  game_id: string;
  played_at: string;
  duration_minutes: number | null;
  player_count: number | null;
  location: string | null;
  result: string | null;
  score: number | null;
  opponent_score: number | null;
  notes: string | null;
  players: Player[] | null;
  created_at: string;
  updated_at: string;
  // Propriedades extras quando joined com games
  game?: {
    id: string;
    name: string;
    image_url: string | null;
    thumb_url: string | null;
    slug: string | null;
  };
}

export interface Player {
  id?: string;
  name: string;
  score?: number | null;
  is_winner?: boolean;
  user_id?: string | null;
}

export interface GameSessionFilters {
  gameId?: string;
  startDate?: string;
  endDate?: string;
  result?: string;
}

export interface GameSessionStats {
  total_plays: number;
  total_wins: number;
  total_losses: number;
  total_draws: number;
  win_rate: number;
  avg_duration: number | null;
  avg_score: number | null;
  high_score: number | null;
  latest_play: string | null;
}

// Interfaces para as estatísticas avançadas
export interface PerformanceTrendPoint {
  period: string;
  win_count: number;
  loss_count: number;
  draw_count: number;
  total_plays: number;
  win_rate: number;
}

export interface FrequentPlayer {
  playerId: string;
  playerName: string;
  gamesTogether: number;
  lastPlayed: string;
  mostPlayedGame: string;
  mostPlayedGameId: string;
  playCount: number;
  winRate: number;
  userId?: string;
  wins: number;
  losses: number;
  sharedSessionsCount: number;
}

export interface ScoreStatistics {
  avg_score: number;
  max_score: number;
  min_score: number;
  median_score: number;
  score_distribution: Record<string, number>;
  win_score_avg: number;
  loss_score_avg: number;
  score_trend: {
    date: string;
    avg_score: number;
  }[];
}

export interface BestGame {
  game_id: string;
  game_name: string;
  slug: string;
  play_count: number;
  wins: number;
  losses: number;
  draws: number;
  win_rate: number;
  avg_duration: number | null;
  last_played: string;
}

// Interfaces para o compartilhamento de sessões
export interface SharedGameSession {
  id: string;
  session_id: string;
  owner_id: string;
  shared_with_id: string;
  accepted: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface PendingSharedSession {
  id: string;
  session_id: string;
  game_name: string;
  game_image: string | null;
  played_at: string;
  owner_id: string;
  owner_name: string;
  created_at: string;
}

// Interface para o resultado da função SQL get_all_game_sessions
export interface GameSessionFromDB {
  id: string;
  game_id: string;
  game_name: string;
  game_image: string | null;
  game_slug: string | null;
  played_at: string;
  duration_minutes: number | null;
  player_count: number | null;
  result: string | null;
  score: number | null;
  owner_id: string;
  is_owner: boolean;
  owner_username: string | null;
  share_status: string | null;
  players: Player[] | null;
}

// <<< Adicionar nova interface para o retorno da RPC com contagem >>>
export interface GameSessionFromDBWithCount extends GameSessionFromDB {
  total_count: number;
}

export interface CombinedGameSession extends GameSession {
  owner_id: string;
  is_owner: boolean;
  owner_username?: string | null;
  share_status?: 'none' | 'pending' | 'accepted_by_some' | 'accepted_by_all'; // Status do compartilhamento (se owner)
}

/**
 * Obtém todas as sessões de jogo de um usuário com paginação
 */
export async function getUserGameSessions(
  userId: string,
  filters: GameSessionFilters = {},
  page: number = 1,
  pageSize: number = 10
): Promise<{ data: GameSession[], count: number }> {
  try {
    let query = supabase
      .from('game_sessions')
      .select(`
        *,
        game:games (id, name, image_url, thumb_url, slug)
      `, { count: 'exact' })
      .eq('user_id', userId)
      .order('played_at', { ascending: false })
      .range((page - 1) * pageSize, page * pageSize - 1);

    // Aplicar filtros se fornecidos
    if (filters.gameId) {
      query = query.eq('game_id', filters.gameId);
    }
    
    if (filters.result) {
      query = query.eq('result', filters.result);
    }
    
    if (filters.startDate) {
      query = query.gte('played_at', filters.startDate);
    }
    
    if (filters.endDate) {
      query = query.lte('played_at', filters.endDate);
    }

    const { data, error, count } = await query;

    if (error) throw error;
    
    return { 
      data: data as GameSession[] ?? [],
      count: count || 0 
    };
  } catch (error) {
    console.error('Erro ao buscar sessões de jogo:', error);
    return { data: [], count: 0 };
  }
}

/**
 * Obtém estatísticas de partidas para um jogo específico
 */
export async function getGameSessionStats(
  userId: string,
  gameId: string
): Promise<GameSessionStats | null> {
  try {
    const { data, error } = await supabase
      .rpc('get_game_session_stats', {
        p_user_id: userId,
        p_game_id: gameId
      });

    if (error) throw error;
    
    return data as unknown as GameSessionStats;
  } catch (error) {
    console.error(`Erro ao obter estatísticas do jogo ${gameId}:`, error);
    return null;
  }
}

/**
 * Adiciona uma nova sessão de jogo
 */
export async function addGameSession(session: Omit<GameSession, 'id' | 'created_at' | 'updated_at' | 'game'>): Promise<GameSession | null> {
  try {
    const sessionDataForInsert = {
      ...session,
      players: session.players as Json
    };

    const { data: savedSession, error } = await supabase
      .from('game_sessions')
      .insert(sessionDataForInsert)
      .select(`
        *,
        game:games (id, name, image_url, thumb_url, slug)
      `)
      .single();

    if (error) {
        console.error('Erro detalhado ao adicionar sessão:', error);
        throw error;
    }
    
    // Chamar gamificação APÓS sucesso da inserção
    if (savedSession) {
        const gamificationResult = await processAction({
            supabase: supabase, 
            userId: savedSession.user_id,
            actionKey: XP_ACTIONS.RECORD_GAME_SESSION,
            sourceId: savedSession.id, 
            details: { 
                gameId: savedSession.game_id,
                duration: savedSession.duration_minutes,
                playerCount: savedSession.player_count,
                result: savedSession.result
            }
        });

        if (!gamificationResult.success) {
            console.warn(`Gamification warning (addGameSession ${savedSession.id}): ${gamificationResult.error}`);
        } else {
             console.log(`Gamification: User ${savedSession.user_id} gained ${gamificationResult.xpGained} XP for recording session ${savedSession.id}`);
        }
    }
    
    return savedSession as GameSession;
  } catch (error) {
    console.error('Erro na função addGameSession:', error);
    return null;
  }
}

/**
 * Atualiza uma sessão de jogo existente
 */
export async function updateGameSession(
  sessionId: string,
  updates: Partial<Omit<GameSession, 'id' | 'user_id' | 'game_id' | 'created_at' | 'updated_at' | 'game'>>
): Promise<GameSession | null> {
  try {
    const updateDataForDb = {
        ...updates,
        players: updates.players !== undefined ? updates.players as Json : undefined
    };

    const { data, error } = await supabase
      .from('game_sessions')
      .update(updateDataForDb)
      .eq('id', sessionId)
      .select(`
        *,
        game:games (id, name, image_url, thumb_url, slug)
      `)
      .single();

    if (error) throw error;
    
    return data as GameSession;
  } catch (error) {
    console.error(`Erro ao atualizar sessão de jogo ${sessionId}:`, error);
    return null;
  }
}

/**
 * Remove uma sessão de jogo
 */
export async function deleteGameSession(sessionId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('game_sessions')
      .delete()
      .eq('id', sessionId);

    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error(`Erro ao excluir sessão de jogo ${sessionId}:`, error);
    return false;
  }
}

/**
 * Obtém as sessões de jogo mais recentes de um usuário
 */
export async function getRecentGameSessions(
  userId: string, 
  limit: number = 5
): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .rpc('get_recent_game_sessions', {
        p_user_id: userId,
        p_limit: limit
      });

    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Erro ao buscar sessões recentes:', error);
    return [];
  }
}

/**
 * Obtém todas as sessões de um jogo específico
 */
export async function getGameSessions(
  userId: string,
  gameId: string
): Promise<GameSession[]> {
  try {
    const { data, error } = await supabase
      .from('game_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .order('played_at', { ascending: false });

    if (error) throw error;
    
    return data as GameSession[];
  } catch (error) {
    console.error(`Erro ao buscar sessões do jogo ${gameId}:`, error);
    return [];
  }
}

/**
 * Obtém tendências de desempenho para um jogo específico.
 * NOTA: A RPC atual 'get_game_performance_trend' parece exigir um game_id.
 * A funcionalidade para tendência geral (sem game_id) pode precisar de outra RPC.
 */
export async function getPerformanceTrend(
  userId: string,
  gameId?: string, // gameId é opcional na assinatura TS
  interval: 'week' | 'month' | 'year' = 'month'
): Promise<PerformanceTrendPoint[]> {
  try {
    let rpcCall;
    if (gameId) {
      rpcCall = supabase
        .rpc('get_game_performance_trend', {
          p_user_id: userId,
          p_game_id: gameId,
          p_interval: interval
        });
    } else {
      // Chamar com p_game_id explicitamente como null
      // Para contornar o problema de tipo estrito, podemos precisar de um cast 
      // ou garantir que a definição da RPC no Supabase para o cliente TS aceite null.
      // Por agora, vamos assumir que a RPC aceita null e o problema é de tipagem no cliente.
      rpcCall = supabase
        .rpc('get_game_performance_trend', {
          p_user_id: userId,
          p_game_id: null as any, // Usando 'as any' para contornar a verificação de tipo estrita
          p_interval: interval
        });
    }

    const { data, error } = await rpcCall;

    if (error) throw error;

    const transformedData = (data || []).map((item: any) => ({
      period: item.period_label || item.period, // Ajustar conforme o nome da coluna retornado pela RPC
      win_count: item.win_count || 0,
      loss_count: item.loss_count || 0,
      draw_count: item.draw_count || 0,
      total_plays: item.total_plays || 0,
      win_rate: item.win_rate || 0,
    }));
    
    return transformedData;

  } catch (error) {
    console.error(`Erro ao obter tendência de desempenho para ${gameId || 'todos os jogos'}:`, error);
    return [];
  }
}

/**
 * Obtém jogadores frequentes para um usuário
 */
export async function getFrequentPlayers(
  userId: string
): Promise<FrequentPlayer[]> {
  try {
    const { data, error } = await supabase
      .rpc('get_frequent_players', {
        p_user_id: userId
      });

    if (error) throw error;
    
    // Mapear resultado da RPC para a interface FrequentPlayer (camelCase)
    // Adicionar coalescência nula para strings obrigatórias
    const mappedData = data?.map((p: any) => ({
        playerId: p.playerid ?? '', // Garantir string
        playerName: p.player_name ?? '', // Garantir string
        gamesTogether: p.games_together ?? 0,
        lastPlayed: p.last_played ?? '', // Garantir string (ou null/undefined se a interface permitir)
        mostPlayedGame: p.most_played_game ?? '', // Garantir string
        mostPlayedGameId: p.most_played_game_id ?? '', // Garantir string
        playCount: p.play_count ?? 0,
        winRate: p.win_rate ?? 0,
        userId: p.user_id ?? undefined, // Garantir string | undefined
        wins: p.wins ?? 0,
        losses: p.losses ?? 0,
        sharedSessionsCount: p.sharedsessionscount ?? 0
    })) || [];

    return mappedData as FrequentPlayer[]; // A asserção deve ser segura agora
  } catch (error) {
    console.error(`Erro ao obter jogadores frequentes para ${userId}:`, error);
    return [];
  }
}

/**
 * Obtém estatísticas detalhadas de pontuação para um jogo
 */
export async function getScoreStatistics(
  userId: string,
  gameId: string
): Promise<ScoreStatistics | null> {
  try {
    const { data, error } = await supabase
      .rpc('get_score_statistics', {
        p_user_id: userId,
        p_game_id: gameId
      });

    if (error) throw error;
    
    const result = Array.isArray(data) ? data[0] : data;
    
    return result as unknown as ScoreStatistics;
  } catch (error) {
    console.error(`Erro ao obter estatísticas de pontuação para o jogo ${gameId}:`, error);
    return null;
  }
}

/**
 * Obtém os melhores jogos por taxa de vitória
 */
export async function getBestGamesByWinrate(
  userId: string,
  minPlays: number = 3
): Promise<BestGame[]> {
  try {
    const { data, error } = await supabase
      .rpc('get_best_games_by_winrate', {
        p_user_id: userId,
        p_min_plays: minPlays
      });

    if (error) throw error;
    
    return data as BestGame[];
  } catch (error) {
    console.error('Erro ao obter melhores jogos por taxa de vitória:', error);
    return [];
  }
}

// Função para compartilhar uma sessão de jogo com outros usuários
export async function shareGameSession(sessionId: string, userIds: string[]): Promise<SharedGameSession[]> {
  try {
    const { data, error } = await supabase
      .rpc('share_game_session', {
        p_session_id: sessionId,
        p_shared_with_ids: userIds,
      });

    if (error) {
      throw error;
    }

    return data as SharedGameSession[] ?? [];
  } catch (error) {
    console.error('Erro ao compartilhar sessão de jogo:', error);
    throw error;
  }
}

// Função para aceitar uma sessão de jogo compartilhada
export async function acceptSharedGameSession(shareId: string): Promise<SharedGameSession> {
  try {
    const { data, error } = await supabase
      .rpc('accept_shared_game_session', {
        p_share_id: shareId,
      });

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Erro ao aceitar sessão compartilhada:', error);
    throw error;
  }
}

// Função para obter uma sessão de jogo específica pelo ID
export async function getGameSessionById(sessionId: string): Promise<GameSession | null> {
  if (!sessionId) {
    console.error('[SESSION_SERVICE] getGameSessionById: sessionId é obrigatório.');
    return null;
  }
  try {
    console.log(`[SESSION_SERVICE] Buscando sessão com ID: ${sessionId}`);
    const { data, error } = await supabase
      .from('game_sessions')
      .select(`
        *,
        game:games (id, name, image_url, thumb_url, slug)
      `)
      .eq('id', sessionId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { 
        console.warn(`[SESSION_SERVICE] Sessão com ID ${sessionId} não encontrada.`);
        return null;
      } 
      console.error(`[SESSION_SERVICE] Erro ao buscar sessão ${sessionId}:`, error);
      throw error;
    }

    console.log(`[SESSION_SERVICE] Sessão ${sessionId} encontrada:`, data);
    return data as GameSession;
  } catch (error) {
    console.error(`[SESSION_SERVICE] Exceção ao buscar sessão ${sessionId}:`, error);
    return null;
  }
}

// Função para obter todas as sessões (próprias e compartilhadas)
export async function getAllGameSessions(
  userId: string,
  filters: GameSessionFilters = {},
  limit: number = 50,
  offset: number = 0
): Promise<{ data: GameSessionFromDB[], count: number }> {
  try {
    console.log(`[sessionService] Buscando sessões para usuário ${userId} com filtros:`, filters);

    const { data, error } = await supabase
      .rpc('get_all_game_sessions', {
        p_user_id: userId,
        p_limit: limit,
        p_offset: offset,
        p_game_id: filters.gameId || undefined,
        p_result: filters.result || undefined,
        p_start_date: filters.startDate || undefined,
      });

    if (error) {
      console.error('[sessionService] Erro ao buscar sessões:', error);
      throw error;
    }

    console.log(`[sessionService] Dados brutos da RPC: ${data?.length || 0} sessões`);
    
    if (data && data.length > 0) {
      console.log('[sessionService] Amostra da primeira sessão recebida:', JSON.stringify(data[0], null, 2));
      
      const isOwnerTypes = data.map((s: any) => ({ 
        id: s.id, 
        is_owner: s.is_owner, 
        tipo: typeof s.is_owner 
      }));
      console.log('[sessionService] Valores de is_owner nos dados brutos:', isOwnerTypes);

      const sharedSessions = data.filter((s: any) => s.is_owner === false);
      console.log(`[sessionService] Sessões compartilhadas encontradas nos dados brutos: ${sharedSessions.length}`);
      if (sharedSessions.length > 0) {
        console.log('[sessionService] Exemplo de sessão compartilhada:', sharedSessions[0]);
      }
    }

    const processedData = (data || []).map((session: any): GameSessionFromDBWithCount => {
      return {
        ...session,
        is_owner: session.is_owner === true,
        total_count: typeof session.total_count === 'number' ? session.total_count : 0
      };
    });

    const totalCount = processedData.length > 0 ? processedData[0].total_count : 0;

    console.log(`[sessionService] Retornando ${processedData.length} sessões, incluindo ${processedData.filter((s: GameSessionFromDBWithCount) => s.is_owner === false).length} compartilhadas`);
    
    return { 
      data: processedData, 
      count: totalCount 
    };

  } catch (error) {
    console.error('[sessionService] Erro ao buscar todas as sessões de jogo:', error);
    throw error;
  }
}

// Função para obter sessões compartilhadas pendentes
export async function getPendingSharedSessions(
  userId: string,
  limit: number = 20
): Promise<PendingSharedSession[]> {
  try {
    const { data, error } = await supabase
      .rpc('get_pending_shared_sessions', {
        p_user_id: userId,
        p_limit: limit,
      });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Erro ao buscar sessões compartilhadas pendentes:', error);
    throw error;
  }
} 